import React from 'react';
import { TravelBundle } from '../../data/go2LegendeMock';
import { Package, Plane, TrendingUp, Star } from 'lucide-react';

interface BundleBuilderProps {
  bundles: TravelBundle[];
}

function calculateSavings(originalPrice: number, bundlePrice: number): number {
  return originalPrice - bundlePrice;
}

function calculateSavingsPercentage(originalPrice: number, bundlePrice: number): number {
  return Math.round(((originalPrice - bundlePrice) / originalPrice) * 100);
}

function findBestValueBundle(bundles: TravelBundle[]): string | null {
  if (bundles.length === 0) return null;

  const bundleWithHighestSavings = bundles.reduce((best, current) => {
    const currentSavingsPercent = calculateSavingsPercentage(current.originalPrice, current.bundlePrice);
    const bestSavingsPercent = calculateSavingsPercentage(best.originalPrice, best.bundlePrice);

    return currentSavingsPercent > bestSavingsPercent ? current : best;
  }, bundles[0]);

  return bundleWithHighestSavings.id;
}

export function BundleBuilder({ bundles }: BundleBuilderProps) {
  const bestValueBundleId = findBestValueBundle(bundles);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-2">
          <Package className="w-6 h-6 text-brand-500" />
          Paket Putovanja
        </h2>
        <p className="text-gray-600">Kombinuj putovanja, uštedi više, zaradi duplo</p>
      </div>

      {/* Bundle Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {bundles.map((bundle) => {
          const savings = calculateSavings(bundle.originalPrice, bundle.bundlePrice);
          const savingsPercent = calculateSavingsPercentage(bundle.originalPrice, bundle.bundlePrice);
          const isBestValue = bundle.id === bestValueBundleId;

          return (
            <div
              key={bundle.id}
              className="relative bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition"
            >
              {/* Best Value Badge */}
              {isBestValue && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md flex items-center gap-1">
                    <TrendingUp className="w-3.5 h-3.5" />
                    Najbolja Ponuda
                  </span>
                </div>
              )}

              {/* Emoji */}
              <div className="text-5xl mb-4 text-center">{bundle.emoji}</div>

              {/* Bundle Name */}
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{bundle.name}</h3>

              {/* Trip List */}
              <div className="mb-5 space-y-2">
                {bundle.trips.map((trip, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-700">
                    <Plane className="w-4 h-4 text-brand-500 flex-shrink-0" />
                    <span className="text-sm font-medium">{trip}</span>
                  </div>
                ))}
              </div>

              {/* Price Comparison */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                {/* Original Price */}
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Originalna cena:</span>
                  <span className="text-sm text-gray-400 line-through">{bundle.originalPrice} EUR</span>
                </div>

                {/* Bundle Price */}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-base font-medium text-gray-900">Cena paketa:</span>
                  <span className="text-2xl font-bold text-brand-500">{bundle.bundlePrice} EUR</span>
                </div>

                {/* Savings */}
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-green-700">Uštedi:</span>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-700">{savings} EUR</div>
                      <div className="text-xs text-green-600">({savingsPercent}% popust)</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Points Bonus */}
              <div className="flex items-center justify-center gap-1.5 mb-2">
                <Star className="w-4 h-4 text-brand-500 fill-brand-500" />
                <span className="text-sm font-medium text-brand-500">
                  +{bundle.bonusPoints} bonus bodova
                </span>
              </div>

              {/* Multiplier */}
              {bundle.pointsMultiplier > 1 && (
                <div className="flex justify-center mb-4">
                  <span className="inline-block bg-go2-yellow text-gray-900 text-sm font-bold px-4 py-1.5 rounded-full">
                    {bundle.pointsMultiplier}x bodovi na sve
                  </span>
                </div>
              )}

              {/* CTA Button */}
              <button className="w-full bg-brand-500 text-white font-bold px-6 py-3 rounded-full hover:bg-brand-600 transition shadow-md">
                Rezerviši Paket
              </button>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {bundles.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Trenutno nema dostupnih paketa</p>
        </div>
      )}
    </div>
  );
}
